/**
 * Infrastructure Tab Prompt Builder
 *
 * Generates a deployment topology diagram showing cloud/DC/network zones
 * with application nodes placed in their hosting environments.
 */

import type { BulkEAImport } from '../../canister-client';
import type { ChatMessage } from '../../canister-client';
import { baseSvgRules, summarizeEntities } from './base-svg-prompt';

export function buildInfrastructurePrompt(data: BulkEAImport): ChatMessage[] {
    const apps = summarizeEntities(data.applications, [
        'name', 'vendor', 'app_type', 'domain', 'criticality',
    ]);
    const techSvcs = summarizeEntities(data.technical_services, [
        'name', 'application', 'service_type', 'availability_sla',
    ]);

    return [
        {
            role: 'user',
            content: `${baseSvgRules()}

DIAGRAM TYPE: Infrastructure Deployment Topology

DATA - Applications:
${apps}

DATA - Technical Services:
${techSvcs}

INSTRUCTIONS:
- Create a deployment topology with zones: Cloud, On-Premise, DMZ, Network.
- Each zone is a large dashed-border container with a zone label.
- Place applications as server/node icons within appropriate zones.
- Infer zone placement from app_type and vendor:
  - SaaS/Cloud vendors → Cloud zone
  - Internal/Custom apps → On-Premise zone
  - APIs and web services → DMZ zone
- Draw network connections between zones.
- Color-code by criticality: red border=Critical, amber=High, blue=Medium.
- Show technical services as small attached badges on their parent apps.
- Add a title "Infrastructure Deployment Topology" at the top.
- Include a zone legend.`,
        },
    ];
}
